import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import api from '../services/api'
import apiClient from '@/services/axios'
import { invalidateAuthCache } from '@/services/authValidationCache'


export const useImpersonationStore = defineStore('impersonation', () => {
  const isImpersonating = ref(false)
  const impersonatedAgent = ref<{
    id: string
    username: string
    role: string
    code?: string
    parent_id?: string
  } | null>(null)

  const PENDING_KEY = 'pending_impersonation'       
  const SESSION_KEY = 'impersonation_session'        
  const SESSION_TOKEN_KEY = 'impersonation_token'    

  /**
   * Restore impersonation state from sessionStorage on app startup / layout mount.
   * Only the tab that picked up the impersonation will have this key.
   */
  const loadFromSession = (): boolean => {
    const sessionData = sessionStorage.getItem(SESSION_KEY)
    if (sessionData) {
      try {
        const agent = JSON.parse(sessionData)
        isImpersonating.value = true
        impersonatedAgent.value = agent
        return true
      } catch {
        sessionStorage.removeItem(SESSION_KEY)
      }
    }
    return false
  }

  /**
   * Admin action (called in the admin's tab):
   * Authenticate as agent using agent credentials, then open a new tab.
   * The admin's tab is NOT modified.
   */
  const impersonateAgent = async (agent: {
    id: string
    username: string
    role: string
    code?: string
    parent_id?: string
    loginId?: string
    password?: string
  }): Promise<void> => {
    const loginUsername = (agent.username || agent.loginId || '').trim()
    if (!loginUsername || !agent.password) {
      throw new Error('Agent credentials (username/password) are required for impersonation')
    }

    // PROTECTION: Marquer qu'une impersonation a été utilisée dans cette session
    localStorage.setItem('impersonation_was_used', 'true')
    console.log('🛡️ STORE: Set impersonation protection flag')
    
    try {
      // Authenticate as agent to get agent token
      const [loginResponse, configResponse] = await Promise.all([
        axios.post(api.adminLogin(), {
          username: loginUsername,
          password: agent.password,
        }),
        apiClient.get(`${api.agentConfigAddressValue()}?key=agent_redirect_domain`),
      ])

      const { token, role } = loginResponse.data

      if (!token || role !== 'agent') {
        throw new Error('Failed to authenticate as agent')
      }

      // alert(api.agentConfigAddressValue())
      const { data: { data } } = configResponse

      const domain = data

      // Store agent info + agent token temporarily in localStorage (shared between tabs)
      localStorage.setItem(
        PENDING_KEY,
        JSON.stringify({
          agent: {
            id: agent.id,
            username: agent.username,
            role: agent.role,
            code: agent.code,
            parent_id: agent.parent_id,
          },
          token: token, // Agent token, not admin token
          domain,
          // Pass admin's current preferences so the new tab starts with the same look
          preferences: {
            app_local_theme: localStorage.getItem('app_local_theme'),
            app_locale: localStorage.getItem('app_locale'),
          },
          timestamp: Date.now(),
        }),
      )

      console.log('🔐 STORE: Successfully authenticated as agent, token stored for new tab')
    } catch (error) {
      console.error('❌ STORE: Failed to authenticate as agent:', error)
      throw new Error('Failed to authenticate as agent. Please check agent credentials.')
    }

    // Open new tab — ImpersonatePage will pick up the data
    window.open(`${window.location.origin}/impersonate`, '_blank')
  }

  /**
   * Called by ImpersonatePage in the NEW tab:
   * Read pending data from localStorage → move to sessionStorage → clean up.
   */
  const initFromPending = (): { success: boolean; token?: string, domain?: string } => {
    const raw = localStorage.getItem(PENDING_KEY)
    if (!raw) return { success: false }

    try {
      const data = JSON.parse(raw)

      // Expire after 30 seconds to prevent stale pickups
      if (Date.now() - data.timestamp > 30_000) {
        localStorage.removeItem(PENDING_KEY)
        return { success: false }
      }

      // Move to sessionStorage (tab-scoped — only this tab sees it)
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(data.agent))

      // Store the token in sessionStorage so axios reads from here, not localStorage
      if (data.token) {
        invalidateAuthCache()
        sessionStorage.setItem(SESSION_TOKEN_KEY, data.token)
      }

      // Copy admin preferences into sessionStorage so agent tab starts with same theme/locale
      if (data.preferences) {
        if (data.preferences.app_local_theme) {
          sessionStorage.setItem('app_local_theme', data.preferences.app_local_theme)
        }
        if (data.preferences.app_locale) {
          sessionStorage.setItem('app_locale', data.preferences.app_locale)
        }
      }

      // Clear from localStorage so it can't be picked up again
      localStorage.removeItem(PENDING_KEY)

      isImpersonating.value = true
      impersonatedAgent.value = data.agent;

      return {success: true, token: data.token, domain: data.domain}
    } catch {
      localStorage.removeItem(PENDING_KEY)
      return { success: false }
    }
  }

  /**
   * End impersonation: clear sessionStorage and close the tab.
   * The admin's tab with admin_token is still intact.
   */
  const endImpersonation = () => {
    invalidateAuthCache()
    // Nettoyer SEULEMENT sessionStorage - JAMAIS localStorage (admin_token protégé)
    sessionStorage.removeItem(SESSION_KEY)
    sessionStorage.removeItem(SESSION_TOKEN_KEY)
    sessionStorage.removeItem('app_local_theme')
    sessionStorage.removeItem('app_locale')
    isImpersonating.value = false
    impersonatedAgent.value = null
    
    console.log('🛡️ STORE: Agent disconnected - sessionStorage cleared, admin protected')
    
    // SOLUTION ULTIME: Redirection directe vers une page clean (pas de charts)
    window.location.replace('/auth/agent-logout')
  }

  const impersonatedUsername = computed(() => impersonatedAgent.value?.username || '')

  return {
    isImpersonating,
    impersonatedAgent,
    impersonatedUsername,
    loadFromSession,
    impersonateAgent,
    initFromPending,
    endImpersonation,
  }
})
